import '.av{cursor:pointer;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { TextInput } from '../text/TextInput'
import Column from '@app/components/layout/Column'
import { Text } from '../../typography/Text'
import { CardWrapper } from '@app/components/blocks/card/CardWrapper'
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type KeyboardEventHandler,
} from 'react'

export type SearchSelectOption = {
  key: number
  value: string
}

export type SearchSelectProps = {
  label: string
  labelStyle?: Style
  required?: boolean
  errorMessage?: string
  value: string
  setValue: (value: string) => void
  selectOption: (option: SearchSelectOption) => void
  abandonSelection: VoidFunction
  options: SearchSelectOption[]
  noResultsMessage?: { display: boolean; message: string }
}

export const SearchSelect = ({
  label,
  labelStyle,
  required,
  errorMessage,
  value,
  options,
  noResultsMessage,
  setValue,
  selectOption,
  abandonSelection,
}: SearchSelectProps) => {
  const renderPopup = options.length > 0 || noResultsMessage?.display
  const ignoreBlurRef = useRef(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  useEffect(() => {
    if (options.length > 0) {
      setHighlightedIndex(0)
    } else {
      setHighlightedIndex(-1)
    }
  }, [options])

  const handleBlur = () => {
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false
      return
    }
    abandonSelection()
  }

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!renderPopup) {
        return
      }
      if (options.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setHighlightedIndex((i) => (i + 1) % options.length)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setHighlightedIndex((i) => {
            if (i === -1) {
              return options.length - 1
            }
            return (i - 1 + options.length) % options.length
          })
          return
        }
        if (e.key === 'Enter' && highlightedIndex >= 0) {
          e.preventDefault()
          selectOption(options[highlightedIndex])
          return
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        abandonSelection()
      }
    },
    [renderPopup, options, highlightedIndex, selectOption, abandonSelection],
  )

  return (
    <Column gap={0} alignItems="stretch" style={{ position: 'relative' }}>
      <TextInput
        text={value}
        setText={setValue}
        label={label}
        labelStyle={labelStyle}
        required={required}
        errorMessage={errorMessage}
        placeholder={label}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {renderPopup && (
        <CardWrapper
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            width: '100%',
            zIndex: 1,
          }}
        >
          <Column alignItems="stretch">
            {options.map((option, index) => (
              <styled.div className="av"
                key={option.key}
                onMouseDown={(e) => {
                  e.preventDefault()
                  ignoreBlurRef.current = true
                  selectOption(option)
                }}
                style={{
                  ...(index === highlightedIndex && {
                    background: 'var(--gray-100)',
                  }),
                }}
              >
                <Text.Medium>{option.value}</Text.Medium>
              </styled.div>
            ))}
            {noResultsMessage?.display && (
              <Text.Medium color="var(--palette-blue-300)">
                {noResultsMessage.message}
              </Text.Medium>
            )}
          </Column>
        </CardWrapper>
      )}
    </Column>
  )
}
